import React, { useCallback, useLayoutEffect, useState } from "react";
import { IModalInstance } from "@whatslly/ui-components/modals/ModalProvider";
import { BaseModal, ModalHeader } from "@whatslly/ui-components/modals";
import styles from "./ManageRecordTypesModal.module.scss";
import { FormattedRecordType } from "@whatslly/commons/utilClasses/FormattedRecordType";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";
import { Button } from "@whatslly/ui-components/v2/Button";
import type { LayoutFieldsSetupNamesType, LayoutFieldsSetupType } from "@whatslly/types/LayoutFieldsSetupType";
import classnames from "classnames";
import cloneDeep from "lodash/cloneDeep";
import { useTranslation } from "react-i18next";
import { TextToolTipCheck } from "@whatslly/ui-components/TextToolTipCheck";
export const ManageRecordTypesModal: React.FC<
  {
    allRecordTypes: FormattedRecordType[];
    layoutFieldsSetup: LayoutFieldsSetupType;
    type: LayoutFieldsSetupNamesType;
    defaultRecordTypeDevName?: string;
  } & IModalInstance
> = (props) => {
  const { t } = useTranslation();
  const fieldSetupKeys = Object.keys(props.layoutFieldsSetup);
  const [selectedRecords, setSelectedRecordType] = useState<FormattedRecordType[]>(
    props.allRecordTypes.filter(
      (recordType) =>
        recordType.recordTypeSelector === props.defaultRecordTypeDevName || fieldSetupKeys.includes(`${props.type}:${recordType.recordTypeSelector}`)
    )
  );

  const [isDefault, setIsDefault] = useState(props.defaultRecordTypeDevName || "");

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, recordType: FormattedRecordType) => {
      if (!isDefault) setIsDefault(recordType.recordTypeSelector);
      if (e.target.checked) return setSelectedRecordType((prev) => [...prev, recordType]);
      setSelectedRecordType((prev) => prev.filter((recordTypeState) => recordTypeState.recordTypeSelector !== recordType.recordTypeSelector));
    },
    [isDefault]
  );

  const handleSetIsDefault = useCallback(
    (recordType: FormattedRecordType) => {
      const newDefaultIsChecked = selectedRecords.some((selectedRecord) => selectedRecord.recordTypeSelector === recordType.recordTypeSelector);
      if (!newDefaultIsChecked) setSelectedRecordType((prev) => [...prev, recordType]);
      setIsDefault(recordType.recordTypeSelector);
    },
    [selectedRecords]
  );

  const onSave = useCallback(() => {
    const selectedRecordTypesClone = cloneDeep(selectedRecords).map((selectedRecordType) => {
      if (selectedRecordType.recordTypeSelector === isDefault) selectedRecordType.recordTypeSelector = `${isDefault}:default`;
      return selectedRecordType;
    });
    props.closeModal(selectedRecordTypesClone);
  }, [isDefault, props, selectedRecords]);

  useLayoutEffect(() => {
    const defaultIsNotChecked = !selectedRecords.some((selectedRecord) => selectedRecord.recordTypeSelector === isDefault);
    if (defaultIsNotChecked) {
      setIsDefault(selectedRecords[0]?.recordTypeSelector || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecords]);

  return (
    <BaseModal
      className={styles.mainContainer}
      bodyClassName={styles.modalBody}
      closeModal={props.closeModal}
      header={
        <div className={styles.headerContainer}>
          <ModalHeader closeModal={() => props.closeModal()}>{t("record_type_layout.edit_record_type")}</ModalHeader>
        </div>
      }
    >
      <div className={styles.checkboxsWrapper}>
        <div className={styles.selectRecordTypeSubHeader}>{t("record_type_layout.select_record_types_to_display")}</div>
        <div className={styles.checkboxsContainer}>
          {props.allRecordTypes.map((recordType) => (
            <div className={styles.recordTypeCheckBoxRow} key={recordType.recordTypeSelector}>
              <div className={styles.checkBoxAndLabelContainer}>
                <CheckBox
                  checked={selectedRecords.some((selectedRecord) => selectedRecord.recordTypeSelector === recordType.recordTypeSelector)}
                  onChange={(e) => onChange(e, recordType)}
                  data-testid={`record-type-check-box`}
                />
                <TextToolTipCheck text={recordType.name} className={styles.recordTypeName} />
              </div>
              <div
                className={classnames(styles.tag, {
                  [styles.makeDefault]: recordType.recordTypeSelector !== isDefault,
                  [styles.isDefault]: recordType.recordTypeSelector === isDefault,
                })}
                onClick={() => handleSetIsDefault(recordType)}
              >
                {recordType.recordTypeSelector !== isDefault ? t("record_type_layout.make_default") : t("record_type_layout.default")}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.saveButtonContainer}>
        <Button
          text={t("clear_all")}
          custom={{ className: styles.resetButton }}
          onClick={() => {
            setSelectedRecordType([]);
            setIsDefault("");
          }}
        />
        <Button text={t("country_codes.save")} onClick={onSave} testId={`save-selected-record-types`} />
      </div>
    </BaseModal>
  );
};
